import Graph from 'graphology';
import { Attributes, createContext, useEffect, useState } from 'react';
import { Node as APINode, GraphType, Relationship } from '../../lib/api/APITypes';
import { useAppDispatch, useAppSelector } from '../../lib/hooks';
import { FilterDefaultOptions, GraphView } from '../../lib/state/ViewTypes';
import LoadingScreen from '../LoadingScreen';

import { v4 as uuidv4 } from 'uuid';
import {
  useLazyGetNeighboursQuery,
  useLazyGetRecommendedNeighboursQuery,
} from '../../lib/api/GraphAPI';
import { setDocumentStructure } from '../../lib/state/slices/ProjectStateSlice';
import { toaster } from '../../components/ui/toaster';
import { useLocation } from 'react-router-dom';
import TimeLineLayout from './TimeLineLayout';
import ForceDirectedLayout from './ForceDirectedLayout';
import LayoutSwitcher from './LayoutSwticher';
import ListView from './ListView';
import { Box, Text, useToken } from '@chakra-ui/react';

export type GraphLayout = 'LIST_LAYOUT' | 'TIMLINE_LAYOUT' | 'FORCE_DIRECTED_LAYOUT';
const GraphViewContext = createContext<GraphView & { layout: GraphLayout }>({
  centerNode: '',
  id: '',
  name: '',
  floatingDocuments: [],
  serializedGraph: null,
  filter: FilterDefaultOptions,
  layout: 'TIMLINE_LAYOUT',
});

export const GraphPage = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [background] = useToken('colors', ['background']);

  const graphViews = useAppSelector((state) => state.viewReducer.documents.structure.GRAPH);
  const graphViewId = location.pathname.split('/graphs/')[1];
  const graphView = graphViews[graphViewId];
  const isActive = true;

  const [graphLayout, setGraphLayout] = useState<GraphLayout>('TIMLINE_LAYOUT');

  let serializedGraph = graphViews[graphViewId]?.serializedGraph ? graphViews[graphView?.id].serializedGraph : null;
    
  const [
    loadNeighbours,
    loadNeighboursResult,
    lastPromiseInfoLoadNeighbours
  ] = useLazyGetNeighboursQuery({});

  const [
    loadRecommendedNeighbours,
    loadRecommendedNeighboursResult,
    lastPromiseInfoLoadRecommendedNeighbours,
  ] = useLazyGetRecommendedNeighboursQuery();

  const [graphIsLoading, setGraphIsLoading] = useState(true);

  // console.log('Rendering GraphPage for graphViewId:', graphViews, graphViewId, graphView);
  
  // If there is no graph yet, query it
  useEffect(() => {
    if (!graphView?.serializedGraph) {
      if (graphView && graphView.centerNode) {
        loadNeighbours({ id: graphView.centerNode, level: 1 });
      }
    }
  }, [graphView?.serializedGraph]);

  // If the query is running i.e. date is being loaded, enable the loading screen
  useEffect(() => {
    if (loadNeighboursResult.isFetching) {
      setGraphIsLoading(true);
    } else {
      setGraphIsLoading(false);
    }
  }, [loadNeighboursResult.isFetching]);

  useEffect(() => {
    let d: GraphType = { edges: [], nodes: [] };

    if (loadNeighboursResult.data) {
      d.edges.push(...loadNeighboursResult.data.edges);
      d.nodes.push(...loadNeighboursResult.data.nodes);
    }

    if (loadRecommendedNeighboursResult.data) {
      d.nodes.push(...loadRecommendedNeighboursResult.data.nodes);
      let originNode = d.nodes.find(
        (node: APINode) => node.id === lastPromiseInfoLoadRecommendedNeighbours.lastArg,
      );
      loadRecommendedNeighboursResult.data.nodes.forEach((node, index) => {
        d.edges.push({
          elementId: uuidv4(),
          startNodeElementId: originNode!.elementId,
          endNodeElementId: node.elementId,
          type: 'recommend',
          properties: {
            weight: loadRecommendedNeighboursResult.data!.distances[index],
          },
        } as Relationship);
      });
    }

    if (d.nodes.length === 0) {
      setGraphIsLoading(false);
      return;
    }

    if (d) {
      setGraphIsLoading(true);
      
      // Create a new Web Worker
      const worker = new Worker(new URL('./GraphWorker.ts', import.meta.url));

      // Send data to the worker
      worker.postMessage({
        data: d,
        centerNode: graphView!.centerNode,
        serializedMergeGraph: serializedGraph,
      });

      // Handle the result from the worker
      worker.onmessage = (event: MessageEvent<Graph<Attributes, Attributes, Attributes>>) => {
        
        const newGraphView: GraphView = {
          ...graphView,
          serializedGraph: event.data,
          // centerNode: grap
        };

        dispatch(
          setDocumentStructure({
            type: 'GRAPH',
            key: graphViewId,
            value: newGraphView,
          }),
        );

        setGraphIsLoading(false);
        worker.terminate(); // Clean up the worker
      };

      // Handle errors
      worker.onerror = (error) => {
        console.error('Worker error:', error);
        setGraphIsLoading(false);
        worker.terminate();
      };

      return () => {
        worker.terminate();
      };
    }
  }, [loadNeighboursResult.data, loadRecommendedNeighboursResult.data]);

  useEffect(() => {
    if (loadNeighboursResult.isError) {
      toaster.create({
        title: 'Error while loading neighbours',
        description: `Something went wrong while querying node neighbours: ${loadNeighboursResult.error}`,
        type: 'error',
        closable: true,
      });
    }
  }, [loadNeighboursResult.isError]);

  useEffect(() => {
    if (loadRecommendedNeighboursResult.isError) {
      toaster.create({
        title: 'Error while loading neighbours',
        description: `Something went wrong while querying node neighbours: ${loadRecommendedNeighboursResult.error}`,
        type: 'error',
        closable: true,
      });
    }
  }, [loadRecommendedNeighboursResult.isError]);
  
  if (!graphView || !graphView.serializedGraph) {
    return (
      <Box
        height="100%"
        width="100%"
        backgroundColor="background"
        p={4}
        border="1px solid red"
        alignContent="center"
        justifyItems="center"
      >
        <Text>
          Loading in Graph View
        </Text>
      </Box>
    );
  }

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      <GraphViewContext.Provider value={{ ...graphView, layout: graphLayout }}>
        <style>
          {`
            .sigma-container {
              background-color: ${background};
              flex-grow: 1;
            }
          `}
        </style>
        <>
          {graphIsLoading && (
            <LoadingScreen
              style={{
                backgroundColor: background,
                height: '-webkit-fill-available',
                width: '-webkit-fill-available',
                position: 'absolute',
                zIndex: 1000,
              }}
              backdrop={0.8}
            />
          )}
          <LayoutSwitcher
            style={{ position: 'absolute', zIndex: '200', top: 10, left: 10 }}
            layout={graphLayout}
            setLayout={setGraphLayout}
          />
          {graphLayout === 'TIMLINE_LAYOUT' && (
            <TimeLineLayout
              loadNeighbours={loadNeighbours}
              loadRecommendedNeighbours={loadRecommendedNeighbours}
            />
          )}
          {graphLayout === 'FORCE_DIRECTED_LAYOUT' && (
            <ForceDirectedLayout
              loadNeighbours={loadNeighbours}
              loadRecommendedNeighbours={loadRecommendedNeighbours}
            />
          )}
          {graphLayout === 'LIST_LAYOUT' && <ListView />}
        </>
      </GraphViewContext.Provider>
    </div>
  );
};

export { GraphViewContext };
